import { useState, useEffect, useCallback } from "react";
import { api, setAdminPassword, clearAdminPassword, getAdminPassword, formatEuros } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Users, Wrench, Zap, Power, LogOut, Trash2, TrendingUp, Sparkles, Crown, Clock, AlertCircle, EuroIcon } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const COLORS = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
  blue: "border-blue-200 bg-blue-50 text-blue-800",
  indigo: "border-indigo-200 bg-indigo-50 text-indigo-800",
  amber: "border-amber-200 bg-amber-50 text-amber-800",
  slate: "border-slate-200 bg-slate-50 text-slate-800",
  red: "border-red-200 bg-red-50 text-red-800",
};

function MetricCard({ icon: Icon, label, value, sub, color = "slate", testid }) {
  return (
    <Card className={COLORS[color]} data-testid={testid}>
      <CardContent className="py-4">
        <div className="flex items-center gap-2 text-xs uppercase font-semibold opacity-70">
          <Icon className="w-3.5 h-3.5" /> {label}
        </div>
        <div className="font-display text-2xl font-bold mt-1">{value}</div>
        {sub && <div className="text-xs opacity-70 mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function planBadge(plan) {
  switch (plan) {
    case "lifetime":
      return { text: "Vitalicio", cls: "bg-emerald-100 text-emerald-800" };
    case "active":
      return { text: "Suscrito", cls: "bg-blue-100 text-blue-800" };
    case "trial":
      return { text: "Prueba", cls: "bg-amber-100 text-amber-800" };
    case "expired":
      return { text: "Caducado", cls: "bg-red-100 text-red-800" };
    default:
      return { text: plan || "-", cls: "bg-slate-100 text-slate-700" };
  }
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!getAdminPassword());
  const [password, setPassword] = useState("");
  const [workshops, setWorkshops] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [wsRes, mRes] = await Promise.all([
        api.get("/admin/workshops"),
        api.get("/admin/metrics"),
      ]);
      setWorkshops(wsRes.data);
      setMetrics(mRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        clearAdminPassword();
        setAuthed(false);
        toast.error("Sesión de admin caducada");
      } else {
        toast.error("Error al cargar datos");
      }
    }
  }, []);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/login", { password });
      setAdminPassword(password);
      setAuthed(true);
      setPassword("");
      toast.success("Acceso admin concedido");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Contraseña incorrecta");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    clearAdminPassword();
    setAuthed(false);
    setWorkshops([]);
    toast.info("Sesión admin cerrada");
  };

  const toggle = async (w) => {
    await api.put(`/admin/workshops/${w.id}`, { active: !w.active });
    toast.success(`Taller ${!w.active ? "activado" : "desactivado"}`);
    load();
  };

  const remove = async (w) => {
    if (!window.confirm(`¿Eliminar taller "${w.name}" y TODOS sus datos? Esta acción no se puede deshacer.`)) return;
    if (!window.confirm(`Confirma: eliminar ${w.mechanics_count} mecánicos, ${w.repairs_count} citas y ${w.quick_count} tareas rápidas.`)) return;
    await api.delete(`/admin/workshops/${w.id}`);
    toast.success("Taller eliminado");
    load();
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="admin-login">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="font-display text-2xl flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-700" /> Acceso administrador
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={login} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pw">Contraseña</Label>
                <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" data-testid="admin-password-input" autoFocus />
              </div>
              <Button type="submit" disabled={loading} className="h-12 w-full bg-blue-700 hover:bg-blue-800" data-testid="admin-login-btn">
                {loading ? "Comprobando..." : "Entrar"}
              </Button>
              <Link to="/" className="block text-center text-sm text-slate-500 hover:text-slate-700">
                ← Volver a la app
              </Link>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8" data-testid="admin-page">
      <div className="max-w-6xl mx-auto space-y-6">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 flex items-center gap-3">
              <Shield className="w-8 h-8 text-blue-700" /> Panel de Administrador
            </h1>
            <p className="text-slate-500 mt-1">{workshops.length} talleres registrados</p>
          </div>
          <div className="flex gap-2">
            <Link to="/">
              <Button variant="outline" className="h-11">Volver a la app</Button>
            </Link>
            <Button variant="outline" onClick={logout} className="h-11" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
            </Button>
          </div>
        </header>

        {metrics && (
          <section className="space-y-3" data-testid="admin-metrics">
            <h2 className="font-display text-xl font-semibold text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-700" /> Métricas SaaS
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard icon={EuroIcon} label="ARR" value={formatEuros(metrics.arr_cents)} sub="ingresos anuales" color="emerald" testid="metric-arr" />
              <MetricCard icon={EuroIcon} label="MRR" value={formatEuros(metrics.mrr_cents)} sub="mensual equivalente" color="blue" testid="metric-mrr" />
              <MetricCard icon={EuroIcon} label="Últimos 30d" value={formatEuros(metrics.revenue_30d_cents)} sub={`${metrics.new_subs_30d} nuevas suscripciones`} color="indigo" testid="metric-rev-30d" />
              <MetricCard icon={EuroIcon} label="Total cobrado" value={formatEuros(metrics.revenue_total_cents)} sub="histórico" color="slate" testid="metric-rev-total" />
              <MetricCard icon={Clock} label="En prueba" value={metrics.trials_active} sub={`${metrics.new_last_30d} nuevas 30d`} color="amber" testid="metric-trials" />
              <MetricCard icon={Sparkles} label="Suscritos" value={metrics.plan_active} sub={`${metrics.active_ends_30d} renuevan en 30d`} color="blue" testid="metric-active" />
              <MetricCard icon={Crown} label="Vitalicios" value={metrics.plan_lifetime} sub="grandfathered" color="emerald" testid="metric-lifetime" />
              <MetricCard icon={AlertCircle} label="Churn 90d" value={`${(metrics.churn_rate * 100).toFixed(1)}%`} sub={`${metrics.plan_expired} caducadas`} color="red" testid="metric-churn" />
            </div>
          </section>
        )}

        <h2 className="font-display text-xl font-semibold text-slate-900 flex items-center gap-2 pt-2">
          <Users className="w-5 h-5 text-blue-700" /> Talleres
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {workshops.map((w) => {
            const info = w.plan_info || {};
            const badge = planBadge(info.plan || w.plan);
            return (
            <Card key={w.id} data-testid={`admin-workshop-${w.code}`} className={!w.active ? "opacity-60" : ""}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <CardTitle className="font-display text-xl truncate">{w.name}</CardTitle>
                    <div className="font-mono text-sm text-slate-500 mt-1 truncate">{w.code}</div>
                    {w.email && <div className="text-xs text-slate-500 truncate">{w.email}</div>}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${badge.cls}`}>{badge.text}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${w.active ? "bg-green-100 text-green-800" : "bg-slate-200 text-slate-600"}`}>
                      {w.active ? "Activo" : "Inactivo"}
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-2 text-center mb-4">
                  <div className="bg-slate-50 rounded p-2">
                    <Users className="w-4 h-4 mx-auto text-slate-500 mb-1" />
                    <div className="font-bold text-slate-900">{w.mechanics_count}</div>
                    <div className="text-xs text-slate-500">mecánicos</div>
                  </div>
                  <div className="bg-slate-50 rounded p-2">
                    <Wrench className="w-4 h-4 mx-auto text-slate-500 mb-1" />
                    <div className="font-bold text-slate-900">{w.repairs_count}</div>
                    <div className="text-xs text-slate-500">citas</div>
                  </div>
                  <div className="bg-slate-50 rounded p-2">
                    <Zap className="w-4 h-4 mx-auto text-slate-500 mb-1" />
                    <div className="font-bold text-slate-900">{w.quick_count}</div>
                    <div className="text-xs text-slate-500">rápidas</div>
                  </div>
                </div>
                <div className="text-xs text-slate-400 mb-3">
                  Creado: {(w.created_at || "").slice(0, 10)}
                  {info.plan === "trial" && ` · Prueba: ${info.trial_days_left}d restantes`}
                  {info.plan === "active" && info.subscription_current_period_end && ` · Renueva ${info.subscription_current_period_end.slice(0, 10)}`}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => toggle(w)} className="flex-1" data-testid={`toggle-workshop-${w.code}`}>
                    <Power className="w-3 h-3 mr-1" /> {w.active ? "Desactivar" : "Activar"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => remove(w)} className="text-red-600 hover:bg-red-50" data-testid={`delete-workshop-${w.code}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>

        {workshops.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              No hay talleres registrados aún.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
